// Unified activity-type registry — the single source of icon / category /
// title-template metadata for every fixed activity type across BOTH feeds
// that render one:
//   - the org-wide Task Center / Activity Center (Task.type, backend
//     TASK_PUBLISH_TYPES / TASK_LICENSE_TYPES / TASK_STATUS_TYPES / activity
//     types — see backend/app/models/task.py)
//   - a screen's own Activity Log (ScreenLog.event_type — device-level
//     connectivity/content/command events)
//
// These used to be two separately hand-maintained lookup tables (taskRows.tsx's
// TYPE_META and ScreenActivityLog.tsx's EVENT_META) with different shapes and
// different i18n conventions, and — because neither was exhaustive — an
// unmapped type silently fell back to a raw, untranslated slug (e.g.
// "deactivated", which had no entry in EVENT_META at all). One registry means
// one place to add a new type, one guaranteed-translated "unknown type"
// fallback, and one consistent headline shape everywhere: every entry's
// title reads "<Subject> <Verb-ed> ({{name}})" — e.g. "Playlist Published
// (Morning Promo)", "Screen Connected (Lobby TV)" — regardless of which feed
// rendered it. Callers always have SOME name to interpolate: Task rows use
// task.target_label (always set at creation); ScreenLog rows use the most
// specific name available for that event (e.g. content_applied's published-
// content name) falling back to the screen's own name.
import type { LucideIcon } from 'lucide-react'
import {
  Activity, Upload, Archive, RotateCcw, Pencil, MonitorPlay, Volume2,
  Power, PowerOff, Hand, ArrowLeftRight, Trash2, KeyRound, Wifi, WifiOff,
  RefreshCw, AlertTriangle, Radio, Link as LinkIcon, BadgeCheck, HardDrive,
  Camera,
} from 'lucide-react'

// Groups a type by what kind of thing happened — drives filtering (e.g. a
// "Problems only" toggle can just check tone instead of hand-listing types)
// and lets a future cross-feed view (mixing a screen's own events with the
// org tasks that targeted it) reason about types it's never seen a specific
// entry for.
export type ActivityCategory =
  | 'publish'         // content pushed to a screen
  | 'content'         // upload/archive/restore/rename + device content sync
  | 'license'         // seat grant/renew/expire/free
  | 'connectivity'    // online/offline/ping/relink
  | 'device-command'  // dashboard-issued device command (resync/restart/screenshot)
  | 'system'          // admin/lifecycle actions (activate, unpair, cache clear)

// Only the tones apps/frame actually has CSS tokens for (see index.css —
// there's no --info-fg/--stale-fg/--neutral-fg in this app's palette, unlike
// the internal admin's fuller 6-way set). 'neutral' means "no special color,
// just the default muted icon" rather than a dedicated token.
export type ActivityTone = 'success' | 'warning' | 'error' | 'neutral'

export type ActivityTypeMeta = {
  icon: LucideIcon
  category: ActivityCategory
  tone: ActivityTone
  /** i18next key. Its EN value is a "<Subject> <Verb-ed> ({{name}})" template —
   *  pass the specific target name via activityTitle()'s `name` argument. */
  titleKey: string
  titleFallback: string
}

const ACTIVITY_TYPES: Record<string, ActivityTypeMeta> = {
  // ── Publish (Task Center) ────────────────────────────────────────────
  publish_playlist:       { icon: MonitorPlay,    category: 'publish', tone: 'neutral', titleKey: 'activity.type.publishPlaylist',      titleFallback: 'Playlist Published ({{name}})' },
  publish_layout:         { icon: MonitorPlay,    category: 'publish', tone: 'neutral', titleKey: 'activity.type.publishLayout',        titleFallback: 'Layout Published ({{name}})' },
  publish_media:          { icon: MonitorPlay,    category: 'publish', tone: 'neutral', titleKey: 'activity.type.publishMedia',         titleFallback: 'Content Published ({{name}})' },
  publish_menu:           { icon: MonitorPlay,    category: 'publish', tone: 'neutral', titleKey: 'activity.type.publishMenu',          titleFallback: 'Webpage Published ({{name}})' },
  publish_audio:          { icon: Volume2,        category: 'publish', tone: 'neutral', titleKey: 'activity.type.publishAudio',         titleFallback: 'Audio Streamed ({{name}})' },
  publish_audio_playlist: { icon: Volume2,        category: 'publish', tone: 'neutral', titleKey: 'activity.type.publishAudioPlaylist', titleFallback: 'Audio Playlist Streamed ({{name}})' },
  publish_stop_screen:    { icon: Hand,           category: 'publish', tone: 'neutral', titleKey: 'activity.type.publishStopScreen',    titleFallback: 'Stop Screen Published ({{name}})' },
  publish_transition:     { icon: ArrowLeftRight, category: 'publish', tone: 'neutral', titleKey: 'activity.type.publishTransition',    titleFallback: 'Transition Applied ({{name}})' },
  content_expire:         { icon: Trash2,         category: 'publish', tone: 'neutral', titleKey: 'activity.type.contentExpire',        titleFallback: 'Content Removed ({{name}})' },

  // ── Content lifecycle (Task Center + device content sync) ───────────
  upload:          { icon: Upload,        category: 'content', tone: 'neutral', titleKey: 'activity.type.upload',         titleFallback: 'File Uploaded ({{name}})' },
  archive:         { icon: Archive,       category: 'content', tone: 'neutral', titleKey: 'activity.type.archive',        titleFallback: 'Item Archived ({{name}})' },
  restore:         { icon: RotateCcw,     category: 'content', tone: 'neutral', titleKey: 'activity.type.restore',        titleFallback: 'Item Restored ({{name}})' },
  rename:          { icon: Pencil,        category: 'content', tone: 'neutral', titleKey: 'activity.type.rename',         titleFallback: 'Item Renamed ({{name}})' },
  content_updated: { icon: RefreshCw,     category: 'content', tone: 'neutral', titleKey: 'activity.type.contentUpdated', titleFallback: 'Content Updated ({{name}})' },
  content_applied: { icon: MonitorPlay,   category: 'content', tone: 'success', titleKey: 'activity.type.contentApplied', titleFallback: 'Content Received ({{name}})' },
  content_retry:   { icon: RotateCcw,     category: 'content', tone: 'warning', titleKey: 'activity.type.contentRetry',   titleFallback: 'Content Retried ({{name}})' },
  content_error:   { icon: AlertTriangle, category: 'content', tone: 'error',   titleKey: 'activity.type.contentError',   titleFallback: 'Content Error ({{name}})' },

  // ── License ────────────────────────────────────────────────────────
  license_granted: { icon: KeyRound,   category: 'license', tone: 'success', titleKey: 'activity.type.licenseGranted', titleFallback: 'License Granted ({{name}})' },
  license_renewed: { icon: KeyRound,   category: 'license', tone: 'success', titleKey: 'activity.type.licenseRenewed', titleFallback: 'License Renewed ({{name}})' },
  license_expired: { icon: KeyRound,   category: 'license', tone: 'error',   titleKey: 'activity.type.licenseExpired', titleFallback: 'License Expired ({{name}})' },
  license_freed:   { icon: KeyRound,   category: 'license', tone: 'neutral', titleKey: 'activity.type.licenseFreed',   titleFallback: 'License Freed ({{name}})' },
  license_updated: { icon: BadgeCheck, category: 'license', tone: 'success', titleKey: 'activity.type.licenseUpdated', titleFallback: 'License Updated ({{name}})' },

  // ── Connectivity ───────────────────────────────────────────────────
  screen_online:  { icon: Wifi,     category: 'connectivity', tone: 'success', titleKey: 'activity.type.screenOnline',  titleFallback: 'Screen Connected ({{name}})' },
  screen_offline: { icon: WifiOff,  category: 'connectivity', tone: 'error',   titleKey: 'activity.type.screenOffline', titleFallback: 'Screen Disconnected ({{name}})' },
  connected:      { icon: Wifi,     category: 'connectivity', tone: 'success', titleKey: 'activity.type.connected',     titleFallback: 'Screen Connected ({{name}})' },
  offline_alert:  { icon: WifiOff,  category: 'connectivity', tone: 'error',   titleKey: 'activity.type.offlineAlert',  titleFallback: 'Offline Alert ({{name}})' },
  pinged:         { icon: Radio,    category: 'connectivity', tone: 'neutral', titleKey: 'activity.type.pinged',        titleFallback: 'Screen Pinged ({{name}})' },
  relinked:       { icon: LinkIcon, category: 'connectivity', tone: 'neutral', titleKey: 'activity.type.relinked',      titleFallback: 'Device Relinked ({{name}})' },

  // ── Device commands (dashboard → device) ─────────────────────────────
  resync_requested:     { icon: RotateCcw, category: 'device-command', tone: 'neutral', titleKey: 'activity.type.resyncRequested',     titleFallback: 'Sync Retry Requested ({{name}})' },
  restart_requested:    { icon: Power,     category: 'device-command', tone: 'warning', titleKey: 'activity.type.restartRequested',    titleFallback: 'App Restart Requested ({{name}})' },
  screenshot_requested: { icon: Camera,    category: 'device-command', tone: 'neutral', titleKey: 'activity.type.screenshotRequested', titleFallback: 'Screenshot Requested ({{name}})' },

  // ── System / admin ────────────────────────────────────────────────
  screen_activate:   { icon: Power,    category: 'system', tone: 'success', titleKey: 'activity.type.screenActivate',   titleFallback: 'Screen Activated ({{name}})' },
  screen_deactivate: { icon: PowerOff, category: 'system', tone: 'neutral', titleKey: 'activity.type.screenDeactivate', titleFallback: 'Screen Deactivated ({{name}})' },
  activated:         { icon: Power,    category: 'system', tone: 'success', titleKey: 'activity.type.activated',       titleFallback: 'Screen Activated ({{name}})' },
  deactivated:       { icon: PowerOff, category: 'system', tone: 'neutral', titleKey: 'activity.type.deactivated',     titleFallback: 'Screen Deactivated ({{name}})' },
  admin_unpaired:    { icon: Trash2,   category: 'system', tone: 'error',   titleKey: 'activity.type.adminUnpaired',   titleFallback: 'Device Unpaired ({{name}})' },
  cache_evicted:     { icon: HardDrive, category: 'system', tone: 'neutral', titleKey: 'activity.type.cacheEvicted',   titleFallback: 'Cache Cleared ({{name}})' },
}

// Guaranteed-translated fallback for a type that isn't (yet) in the registry
// above — fixes the old bug where an unmapped ScreenLog event_type fell back
// to `type.replace(/_/g, ' ')`, a raw slug that was NEVER run through t() and
// so always showed in English even on an Arabic UI.
const UNKNOWN_ACTIVITY_TYPE: ActivityTypeMeta = {
  icon: Activity, category: 'system', tone: 'neutral',
  titleKey: 'activity.type.unknown', titleFallback: 'Activity ({{name}})',
}

export function activityMetaFor(type: string): ActivityTypeMeta {
  return ACTIVITY_TYPES[type] ?? UNKNOWN_ACTIVITY_TYPE
}

// i18next's t() signature varies by call style; this is the minimal shape
// every caller's `t` satisfies (react-i18next's TFunction included).
type TFn = (key: string, options: Record<string, unknown>) => string

// Builds the unified "<Subject> <Verb-ed> ({{name}})" headline. When no name
// is available (shouldn't normally happen — callers always have at least a
// fallback name in hand) it degrades to the bare action phrase rather than
// showing an empty, awkward "(...)".
export function activityTitle(t: TFn, type: string, name?: string | null): string {
  const meta = activityMetaFor(type)
  const trimmed = (name ?? '').trim()
  if (trimmed) {
    return t(meta.titleKey, { name: trimmed, defaultValue: meta.titleFallback })
  }
  return t(meta.titleKey, { name: '', defaultValue: meta.titleFallback })
    .replace(/\s*\(\s*\)\s*$/, '')
    .trim()
}

// Text-color class for a tone — apps/frame only has success/warning/error
// tokens (see ActivityTone's doc comment); 'neutral' is just the ambient
// muted-foreground color, not a dedicated token.
export function toneTextClass(tone: ActivityTone): string {
  switch (tone) {
    case 'success': return 'text-success-fg'
    case 'warning': return 'text-warning-fg'
    case 'error':   return 'text-error-fg'
    default:        return 'text-muted-foreground'
  }
}
